'use client'

import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { motion, useMotionValueEvent, type MotionStyle } from 'framer-motion'
import { ChevronRight } from 'lucide-react'
import useDrawerSpring from '@/utils/hooks/useDrawerSpring'
import ActivitySidebar from './activity-sidebar'

const desktopQuery = '(min-width: 1024px)'
function subscribeDesktop(callback: () => void) {
	const query = window.matchMedia(desktopQuery)
	query.addEventListener('change', callback)
	return () => query.removeEventListener('change', callback)
}
const desktopSnapshot = () => window.matchMedia(desktopQuery).matches
const serverSnapshot = () => true

export default function HomeActivityPanel({ initialItems }: { initialItems: any[] }) {
	const handleRef = useRef<HTMLButtonElement>(null)
	const [state, setState] = useState<'auto' | 'open' | 'closed'>('auto')
	const [introReady, setIntroReady] = useState(false)
	const desktop = useSyncExternalStore(subscribeDesktop, desktopSnapshot, serverSnapshot)
	const open = state === 'auto' ? desktop && introReady : state === 'open'
	const progress = useDrawerSpring(open)
	// A closed drawer is a full-height fixed panel of rows parked off-screen.
	// iOS Safari keeps painting it, and scrolling the page behind it paints
	// black until the page stops. Take it out of layout once it has slid shut.
	// Open and at rest, the panel drops its transform too: a transformed fixed
	// ancestor keeps iOS from tiling the scrolling list, so rows scrolled into
	// view stay unpainted.
	const [shown, setShown] = useState(open)
	const [settled, setSettled] = useState(false)
	useMotionValueEvent(progress, 'change', value => {
		setShown(value > 0.001)
		setSettled(value > 0.999)
	})

	useEffect(() => {
		if (!desktop || state !== 'auto' || introReady) return
		const timer = window.setTimeout(() => setIntroReady(true), 500)
		return () => window.clearTimeout(timer)
	}, [desktop, state, introReady])

	return (
		<motion.div className="home-activity-panel" data-state={state} data-settled={open && settled ? 'open' : undefined}
			style={{ '--drawer-progress': progress } as MotionStyle}
			onKeyDown={event => {
				if (event.key === 'Escape' && open) {
					handleRef.current?.focus()
					setState('closed')
				}
			}}>
			<button ref={handleRef} type="button" aria-label={open ? 'Hide market activity' : 'Show market activity'}
				aria-expanded={open} aria-controls="market-activity-sidebar"
				onClick={() => setState(open ? 'closed' : 'open')}
				className="activity-edge fixed top-[calc(var(--ow-header-h)+132px)] lg:top-[calc(var(--ow-header-h)+104px)] z-50 flex h-12 w-8 sm:w-4 items-center justify-center rounded-l-md border border-r-0 border-border bg-muted text-muted-foreground shadow-sm hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring">
				<ChevronRight className="size-3" />
			</button>
			<ActivitySidebar open={open} shown={shown || open} initialItems={initialItems} itemsCount={initialItems.length} activitySource="merged" />
		</motion.div>
	)
}
